import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Star,
  CalendarClock,
  Receipt,
  Landmark,
  ShieldCheck,
  Settings,
  Calculator,
  Sparkles,
  MessagesSquare,
  Headset,
  type LucideIcon,
} from 'lucide-react';

import { listDemandesService } from '@/api/contracts';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/api/auth.types';

interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * Préfixes de routes regroupés sous cette entrée (sous-navigation in-page).
   * Sert à garder l'entrée active quand on est sur une route sœur.
   */
  match?: ReadonlyArray<string>;
  /** Restreint l'entrée à ces rôles ; masquée sinon. Absent = visible pour tous. */
  roles?: ReadonlyArray<UserRole>;
}

// Les 10 onglets de la maquette (`ux-app/app/admin-app.jsx`). Les routes
// secondaires (demandes, remplacements, tarifs, pointages, carte, qualité,
// paie) restent accessibles via la sous-navigation `SubTabs` des pages
// parentes — elles ne figurent donc pas dans le menu de gauche.
const ITEMS: ReadonlyArray<NavItem> = [
  { id: 'dashboard', label: 'Tableau de bord', to: '/dashboard', icon: LayoutDashboard },
  { id: 'personnel', label: 'Personnel', to: '/personnel', icon: Users },
  {
    id: 'clients',
    label: 'Clients & contrats',
    to: '/clients',
    icon: Building2,
    match: ['/clients', '/demandes', '/remplacements'],
  },
  {
    id: 'devis',
    label: 'Devis & tarifs',
    to: '/devis',
    icon: FileText,
    match: ['/devis', '/contrats', '/tarifs'],
  },
  { id: 'evaluations', label: 'Notation & primes', to: '/evaluations', icon: Star },
  {
    id: 'missions',
    label: 'Missions',
    to: '/missions',
    icon: CalendarClock,
    match: ['/missions', '/pointages', '/carte', '/qualite'],
  },
  {
    id: 'billing',
    label: 'Facturation',
    to: '/factures',
    icon: Receipt,
    match: ['/factures', '/paie'],
  },
  { id: 'cnps', label: 'CNPS', to: '/cnps', icon: Landmark },
  { id: 'insurance', label: 'Assurance', to: '/assurance', icon: ShieldCheck },
  {
    id: 'finance',
    label: 'Finance',
    to: '/finance',
    icon: Calculator,
    match: ['/finance'],
  },
  {
    id: 'services',
    label: 'Services',
    to: '/services',
    icon: Sparkles,
    match: ['/services'],
  },
  {
    id: 'messagerie',
    label: 'Messagerie (audit)',
    to: '/messagerie',
    icon: MessagesSquare,
  },
  {
    id: 'support',
    label: 'Support agence',
    to: '/support',
    icon: Headset,
  },
  { id: 'config', label: 'Configuration', to: '/config', icon: Settings, roles: ['ADMIN'] },
];

function isItemActive(pathname: string, item: NavItem): boolean {
  const prefixes = item.match ?? [item.to];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function Sidebar(): JSX.Element {
  const { pathname } = useLocation();
  const role = useAuthStore((s) => s.user?.role);

  const nouvellesQuery = useQuery({
    queryKey: ['demandes-service', 'count-nouvelles'],
    queryFn: () => listDemandesService({ page: 1, page_size: 1, statut: 'NOUVELLE' }),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const nbNouvelles = nouvellesQuery.data?.count ?? 0;

  return (
    <aside
      aria-label="Navigation principale"
      className="flex w-60 flex-col bg-navy-700 px-3 py-5"
    >
      <div className="flex items-center gap-3 px-3 pb-5">
        <div
          aria-hidden
          className="grid h-9 w-9 place-items-center rounded-lg bg-gold-500 font-display text-lg font-black text-navy-700"
        >
          H
        </div>
        <span className="font-display text-lg font-extrabold tracking-tightish text-gold-300">
          Harmaury
        </span>
      </div>
      <nav className="flex flex-col gap-1">
        {ITEMS.filter(
          (item) => !item.roles || (role !== undefined && item.roles.includes(role)),
        ).map((item) => {
          const Icon = item.icon;
          const active = isItemActive(pathname, item);
          return (
            <NavLink
              key={item.id}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={clsx(
                'relative flex items-center gap-3 rounded-lg px-3 py-2.5 font-display text-[13px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500',
                active
                  ? 'border-l-[3px] border-gold-500 bg-gold-500/15 pl-[9px] text-gold-300'
                  : 'text-white/75 hover:bg-white/5 hover:text-white',
              )}
            >
              <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {item.id === 'services' && nbNouvelles > 0 && (
                <span
                  aria-label={`${nbNouvelles} demande${nbNouvelles !== 1 ? 's' : ''} de service nouvelle${nbNouvelles !== 1 ? 's' : ''}`}
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1.5 font-display text-[10px] font-extrabold text-navy-700"
                >
                  {nbNouvelles}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
